import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Send, CheckCircle2, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { INTEREST } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  player_name: "",
  parent_name: "",
  email: "",
  phone: "",
  grade_age: "",
  position: "",
  current_team: "",
  sponsorship_interest: false,
  message: "",
};

export default function PlayerInterestDialog({ trigger, testId }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onField = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const reset = () => {
    setForm(empty);
    setSubmitted(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.player_name || !form.email || !form.message) {
      toast.error("Player name, email, and message are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/player-interest`, form);
      setSubmitted(true);
      toast.success("Sent privately to Coach Bledsoe.");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild data-testid={testId}>
        {trigger}
      </DialogTrigger>
      <DialogContent
        data-testid={INTEREST.dialog}
        className="max-w-2xl bg-[#0A1128] border border-white/15 text-white p-0 overflow-hidden"
      >
        <div className="px-7 sm:px-10 pt-8 pb-6 border-b border-white/10 bg-[#050B14]/60">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#93A5C0]">
            <Lock size={13} /> Private · Direct to Coach
          </div>
          <DialogHeader className="mt-3 space-y-2 text-left">
            <DialogTitle className="font-display text-2xl sm:text-3xl tracking-tight">
              Player Interest
            </DialogTitle>
            <DialogDescription className="text-[#A0ABC0] text-sm">
              Send a private message to the BBA program director. Ask about
              training, travel team tryouts, or full player sponsorship.
            </DialogDescription>
          </DialogHeader>
        </div>

        {submitted ? (
          <div
            data-testid={INTEREST.success}
            className="px-7 sm:px-10 py-14 text-center"
          >
            <div className="w-14 h-14 mx-auto grid place-items-center border border-white/40 text-white mb-5">
              <CheckCircle2 size={26} />
            </div>
            <h4 className="font-display text-2xl">Message received privately.</h4>
            <p className="text-[#A0ABC0] mt-3 max-w-md mx-auto text-sm">
              Coach Bledsoe will review and reach out directly. Thank you for
              your interest in Bledsoe Basketball Academy.
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={INTEREST.closeSuccess}
              className="btn-gold mt-8 px-6 py-3 text-sm uppercase"
            >
              Close
            </button>
          </div>
        ) : (
          <form
            data-testid={INTEREST.form}
            onSubmit={onSubmit}
            className="px-7 sm:px-10 py-7 space-y-5 max-h-[70vh] overflow-y-auto"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Player Name *">
                <input
                  type="text"
                  required
                  data-testid={INTEREST.inputPlayer}
                  value={form.player_name}
                  onChange={onField("player_name")}
                  placeholder="e.g. Jordan Smith"
                  className="bba-input w-full mt-2 px-4 py-3 text-sm"
                />
              </Field>
              <Field label="Parent Name">
                <input
                  type="text"
                  data-testid={INTEREST.inputParent}
                  value={form.parent_name}
                  onChange={onField("parent_name")}
                  placeholder="Optional"
                  className="bba-input w-full mt-2 px-4 py-3 text-sm"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Email *">
                <input
                  type="email"
                  required
                  data-testid={INTEREST.inputEmail}
                  value={form.email}
                  onChange={onField("email")}
                  placeholder="you@email.com"
                  className="bba-input w-full mt-2 px-4 py-3 text-sm"
                />
              </Field>
              <Field label="Phone">
                <input
                  type="tel"
                  data-testid={INTEREST.inputPhone}
                  value={form.phone}
                  onChange={onField("phone")}
                  placeholder="(555) 555-5555"
                  className="bba-input w-full mt-2 px-4 py-3 text-sm"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              <Field label="Grade / Age">
                <input
                  type="text"
                  data-testid={INTEREST.inputGrade}
                  value={form.grade_age}
                  onChange={onField("grade_age")}
                  placeholder="8th Grade"
                  className="bba-input w-full mt-2 px-4 py-3 text-sm"
                />
              </Field>
              <Field label="Position">
                <input
                  type="text"
                  data-testid={INTEREST.inputPosition}
                  value={form.position}
                  onChange={onField("position")}
                  placeholder="Guard / Wing / Post"
                  className="bba-input w-full mt-2 px-4 py-3 text-sm"
                />
              </Field>
              <Field label="Current Team">
                <input
                  type="text"
                  data-testid={INTEREST.inputTeam}
                  value={form.current_team}
                  onChange={onField("current_team")}
                  placeholder="School or club"
                  className="bba-input w-full mt-2 px-4 py-3 text-sm"
                />
              </Field>
            </div>

            <label className="flex items-start gap-3 border border-white/15 bg-white/[0.02] px-4 py-3 cursor-pointer hover:border-white/30 transition-colors">
              <input
                type="checkbox"
                checked={form.sponsorship_interest}
                onChange={onField("sponsorship_interest")}
                data-testid={INTEREST.inputSponsorship}
                className="mt-1 accent-white"
              />
              <div>
                <div className="flex items-center gap-2 text-sm text-white">
                  <ShieldCheck size={14} className="text-[#B8C5D6]" />
                  I'd like to learn about full player sponsorship
                </div>
                <div className="text-xs text-[#A0ABC0] mt-1">
                  BBA offers full player sponsorship opportunities on a
                  select basis.
                </div>
              </div>
            </label>

            <Field label="Message *">
              <textarea
                required
                data-testid={INTEREST.inputMessage}
                value={form.message}
                onChange={onField("message")}
                rows={4}
                placeholder="Share your goals, background, and questions..."
                className="bba-input w-full mt-2 px-4 py-3 text-sm resize-none"
              />
            </Field>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid={INTEREST.submit}
                className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm uppercase disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send Privately"}
                <Send size={15} />
              </button>
              <div className="text-xs text-[#A0ABC0] inline-flex items-center gap-2">
                <Lock size={12} /> Only visible to Coach Bledsoe & staff.
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] tracking-[0.22em] uppercase text-white/60">
      {label}
    </label>
    {children}
  </div>
);
